import { computed, inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { filter, firstValueFrom, take } from 'rxjs';
import { appRouteCommands } from '../app-route-paths';
import { MeDto } from '../connectors/backend';
import { AuthService } from '../services/auth-service';
import { CompanyService } from '../services/company.service';

/**
 * Resolves once we know whether a company exists. Called before the first
 * `await` so `toObservable` still sees the guard's injection context.
 */
function waitForCompaniesResolved(companyService: CompanyService) {
  return firstValueFrom(
    toObservable(companyService.companiesResolved).pipe(
      filter(Boolean),
      take(1),
    ),
  );
}

/**
 * Resolves with the signed-in user and the selected company once both are known. Without an
 * `injector` it must run in an injection context.
 */
export function waitForMeAndCompany(
  injector = inject(Injector),
): Promise<{ me: MeDto; companyId: string }> {
  const me = injector.get(AuthService).me;
  const companyId = injector.get(CompanyService).companyId;
  const known = () => {
    const user = me();
    const company = companyId();
    return user && company ? { me: user, companyId: company } : null;
  };

  const now = known();
  if (now) return Promise.resolve(now);
  return firstValueFrom(
    toObservable(computed(known), { injector }).pipe(filter(Boolean), take(1)),
  );
}

/**
 * Every page is scoped to a company, so without one they would all render empty
 * with no explanation.
 */
export const companyRequiredGuard: CanActivateFn = async () => {
  const companyService = inject(CompanyService);
  const router = inject(Router);
  const resolved = waitForCompaniesResolved(companyService);

  await resolved;

  if (!companyService.hasNoCompany()) return true;
  return router.createUrlTree(appRouteCommands.noCompany());
};

/**
 * Keeps the no-company page reachable only while it applies: once a company
 * turns up the page would be a dead end.
 */
export const noCompanyOnlyGuard: CanActivateFn = async () => {
  const companyService = inject(CompanyService);
  const router = inject(Router);
  const resolved = waitForCompaniesResolved(companyService);

  await resolved;

  if (companyService.hasNoCompany()) return true;
  return router.createUrlTree(appRouteCommands.root());
};
